const extractId = (item) => {
  if (item == null) return null;
  if (typeof item === "object") return item.id ?? null;
  return item;
};

const isRecord = (value) => value != null && typeof value === "object" && !Array.isArray(value);

const hasBulkDeleteResultShape = (value) => {
  if (!isRecord(value)) return false;
  if (
    Array.isArray(value.deleted) ||
    Array.isArray(value.skipped) ||
    Array.isArray(value.errors) ||
    Array.isArray(value.not_found)
  ) {
    return true;
  }
  if (typeof value.message === "string" && value.message.trim().length > 0) {
    return true;
  }
  if (value.status === 200 || value.status === "200") {
    return (
      "deleted" in value ||
      "skipped" in value ||
      "errors" in value ||
      "not_found" in value ||
      "message" in value
    );
  }
  return false;
};

const isRequestDeletePayload = (value) =>
  isRecord(value) &&
  (Array.isArray(value.invoice_ids) ||
    Array.isArray(value.document_ids) ||
    value.status_filter != null) &&
  !hasBulkDeleteResultShape(value);

const toList = (value) => {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === "object") return [value];
  return [];
};

export const extractBulkDeleteResponsePayload = (payload) => {
  if (payload == null) return null;

  if (typeof payload === "string") {
    try {
      return extractBulkDeleteResponsePayload(JSON.parse(payload));
    } catch {
      return null;
    }
  }

  if (Array.isArray(payload)) return payload;

  if (typeof payload !== "object") return null;

  if (hasBulkDeleteResultShape(payload)) {
    return payload;
  }

  if (isRequestDeletePayload(payload)) {
    return null;
  }

  const nested = payload.data ?? payload.response ?? payload.result ?? payload.body;
  if (nested != null && nested !== payload) {
    return extractBulkDeleteResponsePayload(nested);
  }

  return payload;
};

export const normalizeDeletedIds = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map(extractId).filter((id) => id != null);
  }
  if (payload && typeof payload === "object" && Array.isArray(payload.deleted)) {
    return payload.deleted.map(extractId).filter((id) => id != null);
  }
  return [];
};

export const parseBulkDeleteResult = (payload) => {
  const source = extractBulkDeleteResponsePayload(payload);

  if (source == null) {
    return {
      deletedIds: [],
      deleted: [],
      skipped: [],
      errors: [],
      not_found: [],
      message: "",
      deletedCount: 0,
      skippedCount: 0,
      errorCount: 0,
      notFoundCount: 0,
    };
  }

  const deletedIds = normalizeDeletedIds(source);

  if (Array.isArray(source)) {
    return {
      deletedIds,
      deleted: source,
      skipped: [],
      errors: [],
      not_found: [],
      message: "",
      deletedCount: deletedIds.length,
      skippedCount: 0,
      errorCount: 0,
      notFoundCount: 0,
    };
  }

  const deleted = toList(source?.deleted);
  const skipped = toList(source?.skipped);
  const errors = toList(source?.errors);
  const not_found = toList(source?.not_found);
  const message = typeof source?.message === "string" ? source.message.trim() : "";

  return {
    deletedIds,
    deleted,
    skipped,
    errors,
    not_found,
    message,
    deletedCount: deleted.length,
    skippedCount: skipped.length,
    errorCount: errors.length,
    notFoundCount: not_found.length,
  };
};

export const shouldShowBulkDeleteResult = (result) => {
  if (!result) return false;

  const { deletedCount, skippedCount, errorCount, notFoundCount, message } = result;

  if (skippedCount > 0 || errorCount > 0 || notFoundCount > 0) {
    return true;
  }

  if (deletedCount === 0 && (skippedCount > 0 || Boolean(message))) {
    return true;
  }

  if (deletedCount > 0 && skippedCount > 0) {
    return true;
  }

  return false;
};
